<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'
import { VueTelInput } from 'vue-tel-input'
import 'vue-tel-input/vue-tel-input.css'
import UserService from '@/services/UserService'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormProgress from '@/components/Atoms/FormProgress.vue'
import Spinner from '@/components/ui/Spinner.vue'
import Alert from '@/components/Atoms/Alert.vue'
import TurnstileWidget from '@/components/ui/TurnstileWidget.vue'
import OtpInput from '@/components/ui/OtpInput.vue'
import FloatingInput from '@/components/ui/FloatingInput.vue'
import GoogleAuthButton from '@/components/ui/GoogleAuthButton.vue'
import { TURNSTILE_SITE_KEY } from '@/Utils/captcha'
import { redirectAfterAuth } from '@/Utils/authRedirect'
import { submitSignupToHubspot } from '@/Utils/hubspot'
import { track, EVENTS } from '@/lib/tracking'
import { isPlausiblePhone } from '@/lib/phonePlausibility'

const router = useRouter()
const { notify } = useNotification()

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_INVALID_MSG = 'Enter a valid phone number.'

// Turnstile bot protection. Enabled in production only: the site key is
// scoped to the production domain, so localhost/staging can't pass it.
const captchaEnabled = process.env.VUE_APP_API_ENV === 'prod'

// vue-tel-input config, mirrored from SignUp.vue so the post-Google phone
// step looks and behaves identically.
const telOptions = {
	showDialCodeInList: true,
	showDialCodeInSelection: true,
	showFlags: true,
	showSearchBox: true,
}

// The bundled country list appends native names, e.g. "Germany (Deutschland)".
// Strip the trailing parenthetical so the dropdown shows English names only.
function stripNativeName(name) {
	const trimmed = name.trim()
	if (!trimmed.endsWith(')')) return trimmed
	let depth = 0
	for (let i = trimmed.length - 1; i >= 0; i--) {
		if (trimmed[i] === ')') depth++
		else if (trimmed[i] === '(') {
			depth--
			if (depth === 0) return trimmed.slice(0, i).trim()
		}
	}
	return trimmed
}

let englishCountries
try {
	englishCountries = VueTelInput.props.allCountries
		.default()
		.map((c) => ({ ...c, name: stripNativeName(c.name) }))
} catch (e) {
	englishCountries = undefined
}

// Pin the countries Castr customers most commonly stream from to the top.
const preferredCountries = [
	'US',
	'CA',
	'GB',
	'DE',
	'AU',
	'NL',
	'FR',
	'IN',
	'BR',
	'AE',
]

const step = ref('email') // 'email' | 'password' | 'code'
const email = ref('')
const code = ref('')
const password = ref('')
// Set when /login-code/request answers { loginMethod: 'password' } — such
// accounts must send forceCode on any subsequent code request (incl. resend),
// otherwise the backend answers with the password hint again instead of a code.
const passwordAccount = ref(false)
const captchaToken = ref('')
const processing = ref(false)
const error = ref('')
const lockedError = ref('')
// Show the "Signing you in…" state on first paint when we arrive with an
// a_token (e.g. the Google SSO callback), so the login form never flashes.
const autoLoginTriggered = ref(!!router.currentRoute.value.query?.a_token)
const resendCountdown = ref(0)
const verifySuccess = ref(false)
const showSpamHint = ref(false)
const turnstile = ref(null)
const codeInput = ref(null)
const emailInput = ref(null)
const passwordInput = ref(null)
let resendTimer = null
let spamHintTimer = null

// Required post-Google phone step: shown once, right after a token login for a
// new account with no phone on file. There is no skip; it is deliberately not
// persisted or route-guarded, so a later login goes straight to the dashboard.
const showPhoneStep = ref(false)
const savingPhone = ref(false)
const phoneError = ref('')
const phoneInputValue = ref('')
const phoneNumber = ref('')
const phoneValid = ref(false)
const phoneStepInput = ref(null)
// Captured when the phone step is triggered (i.e. a phone-less, effectively new
// Google signup) so we can forward the lead to HubSpot once the phone is saved.
const pendingSignupEmail = ref('')
const pendingSignupName = ref('')

// Mask for shoulder-surfing privacy: "govinda@castr.com" → "g•••••@castr.com"
const maskedEmail = computed(() => {
	const value = (email.value || '').trim()
	const at = value.indexOf('@')
	if (at < 1) return value
	return `${value[0]}•••••${value.slice(at)}`
})

const emailValid = computed(() => EMAIL_RE.test((email.value || '').trim()))
const canRequestCode = computed(
	() =>
		emailValid.value &&
		(!captchaEnabled || !!captchaToken.value) &&
		!processing.value,
)
const codeValid = computed(() => /^\d{6}$/.test(code.value))

function onCaptchaVerified(token) {
	captchaToken.value = token
}
function onCaptchaExpired() {
	captchaToken.value = ''
}

function onEmailBlur() {
	const value = (email.value || '').trim()
	if (value && !EMAIL_RE.test(value)) {
		error.value = 'Please enter a valid email.'
	}
}

function startResendCountdown() {
	resendCountdown.value = 60
	clearInterval(resendTimer)
	resendTimer = setInterval(() => {
		resendCountdown.value -= 1
		if (resendCountdown.value <= 0) clearInterval(resendTimer)
	}, 1000)
}

// Typing a fresh digit dismisses the previous "wrong code" error.
watch(code, (value) => {
	if (value && error.value) error.value = ''
})
// Same for the password field ("Incorrect password." goes away on retype).
watch(password, (value) => {
	if (value && error.value) error.value = ''
})

// Per-step page title + delayed "check spam" nudge on the code step.
watch(
	step,
	(value) => {
		document.title =
			value === 'code' ? 'Check your email – Castr' : 'Log in – Castr'
		clearTimeout(spamHintTimer)
		showSpamHint.value = false
		if (value === 'code') {
			spamHintTimer = setTimeout(() => {
				showSpamHint.value = true
			}, 20000)
		}
	},
	{ immediate: true },
)

onBeforeUnmount(() => {
	clearInterval(resendTimer)
	clearTimeout(spamHintTimer)
})

function handleAuthError(err) {
	const message = err?.message || 'Something went wrong. Please try again.'
	if (err?.code === 403) {
		lockedError.value = message
	} else {
		error.value = message
	}
}

async function requestCode() {
	error.value = ''
	lockedError.value = ''
	if (!emailValid.value) {
		error.value = 'Please enter a valid email.'
		return
	}
	if (captchaEnabled && !captchaToken.value) {
		error.value = 'Please complete the security check.'
		return
	}
	processing.value = true
	try {
		const response = await UserService.requestLoginCode(email.value.trim())

		// Accounts that enabled password login get { loginMethod: 'password' }
		// and no email — show the password step instead of the code step.
		if (response?.loginMethod === 'password') {
			passwordAccount.value = true
			step.value = 'password'
			processing.value = false
			await nextTick()
			passwordInput.value?.focus()
			return
		}

		passwordAccount.value = false
		track(EVENTS.LOGIN_CODE_REQUESTED, { is_resend: false })
		step.value = 'code'
		// Clear the loading state BEFORE focusing: the OTP cells are disabled
		// while `processing` is true (via the `verifying` prop), and a disabled
		// input can't receive focus. Focusing must happen after this flips false
		// so the field is ready to type without an extra click.
		processing.value = false
		startResendCountdown()
		await nextTick()
		codeInput.value?.focus()
	} catch (err) {
		handleAuthError(err)
		turnstile.value?.reset()
		captchaToken.value = ''
		processing.value = false
	}
}

async function resendCode() {
	if (resendCountdown.value > 0 || processing.value) return
	error.value = ''
	lockedError.value = ''
	processing.value = true
	try {
		// forceCode keeps resends working for password-enabled accounts, which
		// otherwise get the { loginMethod: 'password' } answer and no email.
		await UserService.requestLoginCode(email.value.trim(), false, {
			forceCode: passwordAccount.value,
		})
		track(EVENTS.LOGIN_CODE_REQUESTED, { is_resend: true })
		startResendCountdown()
		notify({ group: 'success', text: 'A new code is on its way.' })
	} catch (err) {
		handleAuthError(err)
	}
	processing.value = false
}

// "Email me a code instead" on the password step — both methods stay usable.
async function useCodeInstead() {
	if (processing.value) return
	error.value = ''
	lockedError.value = ''
	processing.value = true
	try {
		await UserService.requestLoginCode(email.value.trim(), false, {
			forceCode: true,
		})
		track(EVENTS.LOGIN_CODE_REQUESTED, { is_resend: false })
		track(EVENTS.LOGIN_PASSWORD_CODE_FALLBACK, {})
		password.value = ''
		step.value = 'code'
		processing.value = false
		startResendCountdown()
		await nextTick()
		codeInput.value?.focus()
	} catch (err) {
		handleAuthError(err)
		processing.value = false
	}
}

async function submitPassword() {
	if (processing.value || verifySuccess.value) return
	error.value = ''
	lockedError.value = ''
	if (!password.value) {
		error.value = 'Please enter your password.'
		return
	}
	processing.value = true
	try {
		const response = await UserService.loginWithPassword(
			email.value.trim(),
			password.value,
		)
		const { user, authKey } = response
		UserService.setSession(user, authKey)
		track(EVENTS.LOGIN_PASSWORD_VERIFIED, {
			two_factor_required: !!response.twoFactorRequired,
		})

		// Same TOTP hand-off as the code flow.
		if (response.twoFactorRequired) {
			localStorage.setItem('token', authKey)
			localStorage.setItem('twoFactorRequired', true)
			if (user?.twoFactorValidated) {
				localStorage.setItem('twoFactorValidated', true)
			}
			router.push({ path: '/two-factor-verification' })
			return
		}

		processing.value = false
		track(EVENTS.LOGIN_COMPLETED, { method: 'password' })
		notify({ group: 'success', text: 'Successfully logged in' })
		autoLoginTriggered.value = true
		redirectAfterAuth()
	} catch (err) {
		// Generic 401 covers wrong password (and, server-side, accounts without
		// the flag). 429 carries the lockout retry-after text; 403 is locked.
		if (err?.code === 401) {
			error.value = 'Incorrect password.'
		} else {
			handleAuthError(err)
		}
		processing.value = false
	}
}

function editEmail() {
	step.value = 'email'
	code.value = ''
	password.value = ''
	passwordAccount.value = false
	error.value = ''
	lockedError.value = ''
	verifySuccess.value = false
	nextTick(() => emailInput.value?.focus())
}

async function verifyCode() {
	if (processing.value || verifySuccess.value) return
	error.value = ''
	lockedError.value = ''
	if (!codeValid.value) {
		error.value = 'Please enter the 6-digit verification code.'
		return
	}
	processing.value = true
	try {
		const response = await UserService.verifyLoginCode(
			email.value.trim(),
			code.value,
		)
		const { user, authKey } = response
		UserService.setSession(user, authKey)
		track(EVENTS.LOGIN_CODE_VERIFIED, {
			two_factor_required: !!response.twoFactorRequired,
			method: 'email_otp',
		})

		// Preserve the existing TOTP two-factor step when the backend asks for it.
		if (response.twoFactorRequired) {
			localStorage.setItem('token', authKey)
			localStorage.setItem('twoFactorRequired', true)
			if (user?.twoFactorValidated) {
				localStorage.setItem('twoFactorValidated', true)
			}
			router.push({ path: '/two-factor-verification' })
			return
		}

		// Flash the cells green, then hand off to the redirect screen.
		verifySuccess.value = true
		processing.value = false
		track(EVENTS.LOGIN_COMPLETED, { method: 'email_otp' })
		notify({ group: 'success', text: 'Successfully logged in' })
		setTimeout(() => {
			autoLoginTriggered.value = true
			redirectAfterAuth()
		}, 500)
	} catch (err) {
		handleAuthError(err)
		processing.value = false
		// Wrong code: shake, clear, and refocus so the user can retype at once.
		codeInput.value?.shake()
		codeInput.value?.clear()
	}
}

function goToDashboard() {
	window.location.href = window.location.origin + '/app/dashboard'
}

// After a Google auto-login, restore any invite/continue context stashed by
// GoogleAuthButton before the OAuth round-trip and route through the same
// mechanism the email flow uses, so a team invite reaches the accept pop-up.
// No stash (normal Google login) falls back to the plain dashboard redirect.
function finishRedirect() {
	const stashed = localStorage.getItem('pendingInviteQuery')
	if (!stashed) {
		goToDashboard()
		return
	}
	localStorage.removeItem('pendingInviteQuery')
	const query = {}
	new URLSearchParams(stashed).forEach((value, key) => {
		query[key] = value
	})
	redirectAfterAuth({ query })
}

function onPhoneValidate(mobile) {
	phoneValid.value = !!mobile?.valid && isPlausiblePhone(mobile.number)
	if (phoneValid.value) {
		phoneNumber.value = mobile.number
	}
}

// Typing again clears the last error; blur/Enter re-check what's left.
watch(phoneInputValue, () => {
	phoneError.value = ''
})

function onPhoneBlur() {
	if (/\d/.test(phoneInputValue.value || '') && !phoneValid.value) {
		phoneError.value = PHONE_INVALID_MSG
	}
}

async function savePhoneAndContinue() {
	if (savingPhone.value) return
	if (!phoneValid.value) {
		phoneError.value = PHONE_INVALID_MSG
		return
	}
	phoneError.value = ''
	savingPhone.value = true
	try {
		await UserService.createUserContact(phoneNumber.value)
		track(EVENTS.POST_AUTH_PHONE_ADDED, { method: 'auto_token' })
		// Fire-and-forget the new Google signup (now with phone) to HubSpot.
		// Not awaited so it never delays the redirect into the app.
		if (pendingSignupEmail.value) {
			submitSignupToHubspot({
				email: pendingSignupEmail.value,
				name: pendingSignupName.value,
				phone: phoneNumber.value,
			})
		}
		finishRedirect()
	} catch (err) {
		// Backend rejects an already-registered number with 400 "phone already exist"
		// (whether it's on this account or another).
		const msg = String(err?.message || '')
		const isDuplicate = err?.code === 400 && /already exist/i.test(msg)
		phoneError.value = isDuplicate
			? 'That number is already on another account. Use a different number to continue.'
			: msg || 'Could not save your phone number.'
		savingPhone.value = false
	}
}

async function tryAutoLogin(authKey) {
	autoLoginTriggered.value = true
	processing.value = true
	try {
		localStorage.setItem('token', authKey)
		const userProfile = await UserService.getUserInfo()
		UserService.setSession(userProfile, authKey)
		// Google SSO already proves identity, so treat the a_token session as
		// satisfying 2FA: mark it validated so the router guard does not bounce a
		// 2FA-enabled Google user to the TOTP page (which can't work with a full
		// session). Cleared on logout so it never leaks into a later email login.
		localStorage.setItem('twoFactorValidated', 'true')
		track(EVENTS.LOGIN_SESSION_RESTORED, { method: 'auto_token' })

		// Phone capture is only for NEW signups. Detect them purely on the
		// client via account age from joinDate — no backend ?signup flag needed.
		// The 10-min window absorbs client/server clock skew; existing users have
		// far older joinDates and skip straight to the dashboard.
		const joinMs = new Date(userProfile?.joinDate).getTime()
		const isNewSignup = Number.isFinite(joinMs) && (Date.now() - joinMs) < 10 * 60 * 1000
		if (isNewSignup) {
			// Only for accounts with no phone on file. The authoritative source is
			// the "phone" contacts list (what we write to on save), with
			// userProfile.phone as a fast-path fallback.
			let hasPhone = !!String(userProfile?.phone || '').trim()
			if (!hasPhone) {
				try {
					const contacts = await UserService.getUserContacts('phone')
					hasPhone = Array.isArray(contacts) && contacts.length > 0
				} catch (e) {
					hasPhone = false
				}
			}
			if (!hasPhone) {
				// New Google signup: keep the lead's identity for the HubSpot submit
				// that fires once they save the phone step.
				pendingSignupEmail.value = userProfile?.email || ''
				pendingSignupName.value = userProfile?.name || ''
				processing.value = false
				showPhoneStep.value = true
				track(EVENTS.POST_AUTH_PHONE_SHOWN, { method: 'auto_token' })
				await nextTick()
				phoneStepInput.value?.$el?.querySelector('input')?.focus()
				return
			}
		}

		// Brief beat so the "Signing you in…" state reads before the full-page
		// redirect; the token exchange above already covered the real wait.
		setTimeout(finishRedirect, 300)
	} catch (err) {
		// The auth-page 401 handler no longer clears the session (to protect an
		// in-flight token), so drop the token we optimistically set here if the
		// hand-off actually failed, leaving the user on a clean login form.
		localStorage.removeItem('token')
		error.value = err?.message || 'Auto login failed.'
		autoLoginTriggered.value = false
		processing.value = false
	}
}

onMounted(() => {
	const aToken = router.currentRoute.value.query?.a_token
	if (aToken) {
		tryAutoLogin(aToken)
		return
	}
	emailInput.value?.focus()
})
</script>

<template>
	<div class="auth-page">
		<div class="auth-card">
			<div class="flex flex-col items-center text-center mb-7">
				<div class="auth-logo-wrapper">
					<div class="auth-logo-dot"></div>
					<a href="https://castr.com" aria-label="Go to Castr home">
						<img class="auth-logo" src="@/assets/castr.svg" alt="Castr" />
					</a>
				</div>
			</div>

			<template v-if="autoLoginTriggered">
				<!-- Required phone capture after a token login -->
				<template v-if="showPhoneStep">
					<div class="mb-6">
						<h1 class="auth-title">Complete your account</h1>
					</div>

					<form @submit.prevent="savePhoneAndContinue">
						<div class="mb-4">
							<label class="ff-phone-label">Phone number</label>
							<vue-tel-input
								ref="phoneStepInput"
								class="phone-input auth-phone"
								:class="{ 'auth-phone--error': !!phoneError }"
								v-model="phoneInputValue"
								mode="international"
								auto-format
								:dropdown-options="telOptions"
								:preferred-countries="preferredCountries"
								:all-countries="englishCountries"
								:input-options="{ placeholder: '555 000 1234' }"
								@validate="onPhoneValidate"
								@blur="onPhoneBlur"
								@enter="savePhoneAndContinue"
							/>
							<p v-if="phoneError" class="auth-error">{{ phoneError }}</p>
						</div>

						<div class="flex justify-center mt-1">
							<FormButton
								type="main"
								size="lg"
								class="!h-10 !px-14"
								:disabled="!phoneValid || savingPhone"
								@click="savePhoneAndContinue"
							>
								<template v-if="savingPhone">
									Saving
									<Spinner
										text=""
										classes="ml-2"
										spinner-color="currentColor"
										spinner-size="14px"
									/>
								</template>
								<template v-else>Continue</template>
							</FormButton>
						</div>
					</form>
				</template>

				<template v-else>
					<div class="text-center py-6">
						<p class="text-surface-text-2 mb-4">Signing you in…</p>
						<FormProgress :value="100" :max="100" animated class="w-full" />
					</div>
				</template>
			</template>

			<template v-else>
				<!-- Step 1: email + captcha -->
				<template v-if="step === 'email'">
					<div class="mb-6">
						<h1 class="auth-title">Welcome back</h1>
						<p class="auth-subtitle">Log in to your Castr account</p>
					</div>

					<GoogleAuthButton context="login" />

					<Alert
						v-if="lockedError"
						type="error"
						:text="lockedError"
						class="left inline mb-4"
					/>

					<form @submit.prevent="requestCode">
						<div class="mb-4">
							<FloatingInput
								ref="emailInput"
								id="signin-email"
								label="Email address"
								hint="you@company.com"
								v-model="email"
								type="email"
								inputmode="email"
								autocomplete="email"
								:has-error="!!error"
								@update:modelValue="error = ''"
								@blur="onEmailBlur"
							/>
							<p v-if="error" class="auth-error">{{ error }}</p>
						</div>

						<div
							v-if="captchaEnabled"
							class="flex justify-center mb-5 min-h-[65px]"
						>
							<TurnstileWidget
								ref="turnstile"
								:site-key="TURNSTILE_SITE_KEY"
								@verified="onCaptchaVerified"
								@expired="onCaptchaExpired"
								@error="onCaptchaExpired"
							/>
						</div>

						<div class="flex justify-center mt-1">
							<FormButton
								type="main"
								size="lg"
								class="!h-10 !px-14"
								:disabled="!canRequestCode"
								@click="requestCode"
							>
								<template v-if="processing">
									Logging in
									<Spinner
										text=""
										classes="ml-2"
										spinner-color="currentColor"
										spinner-size="14px"
									/>
								</template>
								<template v-else>Log in</template>
							</FormButton>
						</div>
					</form>

					<p class="auth-footer">
						Don't have an account?
						<router-link to="/signup" class="auth-link">Sign up</router-link>
					</p>
					<p class="auth-support">
						Trouble logging in?
						<a href="mailto:hello@castr.io" class="auth-support__link"
							>Contact support</a
						>
					</p>
				</template>

				<!-- Step 2 (password-enabled accounts): password -->
				<template v-else-if="step === 'password'">
					<div class="mb-6">
						<h1 class="auth-title">Enter your password</h1>
						<p class="auth-subtitle">
							Logging in as
							<span class="text-surface-text-1 font-medium">{{ maskedEmail }}</span>
						</p>
					</div>

					<Alert
						v-if="lockedError"
						type="error"
						:text="lockedError"
						class="left inline mb-4"
					/>

					<form @submit.prevent="submitPassword">
						<div class="mb-4">
							<FloatingInput
								ref="passwordInput"
								id="signin-password"
								label="Password"
								v-model="password"
								type="password"
								autocomplete="current-password"
								:has-error="!!error"
							/>
							<p v-if="error" class="auth-error">{{ error }}</p>
						</div>

						<div class="flex justify-center mt-1">
							<FormButton
								type="main"
								size="lg"
								class="!h-10 !px-14"
								:disabled="!password || processing"
								@click="submitPassword"
							>
								<template v-if="processing">
									Logging in
									<Spinner
										text=""
										classes="ml-2"
										spinner-color="currentColor"
										spinner-size="14px"
									/>
								</template>
								<template v-else>Log in</template>
							</FormButton>
						</div>

						<p class="auth-resend mt-6">
							<FormButton
								custom-classes="auth-link font-medium"
								@click="useCodeInstead"
							>
								Email me a code instead
							</FormButton>
						</p>

						<p class="text-center mt-2">
							<router-link to="/forgot-password" class="auth-ghost-btn text-tiny">
								Forgot password?
							</router-link>
						</p>

						<p class="text-center mt-2">
							<FormButton custom-classes="auth-ghost-btn text-tiny" @click="editEmail">
								← Edit email
							</FormButton>
						</p>
					</form>
				</template>

				<!-- Step 2: verification code -->
				<template v-else>
					<div class="mb-6">
						<h1 class="auth-title">Check your email</h1>
						<p class="auth-subtitle">
							We sent a 6-digit code to
							<span class="text-surface-text-1 font-medium">{{ maskedEmail }}</span>.
							It expires in 15 minutes.
						</p>
					</div>

					<form @submit.prevent="verifyCode">
						<OtpInput
							ref="codeInput"
							v-model="code"
							:has-error="!!error"
							:verifying="processing"
							:success="verifySuccess"
							@complete="verifyCode"
						/>
						<p v-if="error" class="auth-error text-center mt-3">{{ error }}</p>

						<div class="flex justify-center mt-6">
							<FormButton
								type="main"
								size="lg"
								class="!h-10 !px-14"
								:disabled="!codeValid || processing || verifySuccess"
								@click="verifyCode"
							>
								<template v-if="processing">
									Verifying
									<Spinner
										text=""
										classes="ml-2"
										spinner-color="currentColor"
										spinner-size="14px"
									/>
								</template>
								<template v-else-if="verifySuccess">Success</template>
								<template v-else>Log in</template>
							</FormButton>
						</div>

						<p class="auth-resend mt-6">
							<span>Didn't get the code?</span>
							<span v-if="resendCountdown > 0" class="auth-resend__wait">
								Resend in {{ resendCountdown }}s
							</span>
							<FormButton
								v-else
								custom-classes="auth-link font-medium"
								@click="resendCode"
							>
								Resend code
							</FormButton>
						</p>

						<transition name="fade">
							<p v-if="showSpamHint" class="auth-spam-hint mt-2">
								Can't find it? Check your spam folder.
							</p>
						</transition>

						<p class="text-center mt-2">
							<FormButton custom-classes="auth-ghost-btn text-tiny" @click="editEmail">
								← Edit email
							</FormButton>
						</p>
					</form>
				</template>
			</template>
		</div>

		<p class="auth-legal">
			<template v-if="captchaEnabled">Protected by Cloudflare. </template>By continuing you agree to Castr's
			<a href="https://castr.com/terms" target="_blank" rel="noopener">Terms</a>
			and
			<a href="https://castr.com/privacypolicy" target="_blank" rel="noopener"
				>Privacy Policy</a
			>.
		</p>
	</div>
</template>

<style scoped>
.auth-page {
	position: relative;
	width: 100%;
	min-height: 100vh;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 40px 16px;
	gap: 18px;
}
/* Ambient brand wash. Sized in percentages and anchored top-centre to match
   the marketing pages (ToolShell, PricingPage); a fixed-pixel circle shrinks
   to a blob on wide displays. Second layer lifts the card off the flat page. */
.auth-page::before {
	content: '';
	position: absolute;
	inset: 0;
	background:
		radial-gradient(65% 45% at 50% 0%, rgba(0, 98, 255, 0.16), transparent 68%),
		radial-gradient(34% 42% at 50% 46%, rgba(0, 98, 255, 0.07), transparent 72%);
	pointer-events: none;
}
.auth-page > * {
	position: relative;
}
.auth-card {
	width: 100%;
	max-width: 384px;
	background-color: var(--castr-surface);
	border: 1px solid var(--castr-border);
	border-radius: 16px;
	box-shadow: 0 24px 60px -24px rgba(0, 0, 0, 0.9);
	padding: 40px 36px 32px;
}
/* Native 120x40 rendering + dot copied verbatim from MainSidebarLogo.vue
   (top-3 left-[116px] size-1) so it matches the dashboard exactly. */
.auth-logo {
	width: 120px;
	height: 40px;
}
.auth-logo-wrapper {
	position: relative;
	display: inline-block;
	line-height: 0;
}
.auth-logo-dot {
	position: absolute;
	width: 4px;
	height: 4px;
	border-radius: 50%;
	left: 116px;
	top: 12px;
	background-color: #ef0000;
	animation: blink 2s ease-in-out infinite;
}
.auth-title {
	font-family: var(--font-display);
	font-size: 24px;
	line-height: 1.15;
	font-weight: 700;
	letter-spacing: -0.02em;
	color: #fff;
	text-align: center;
	margin-bottom: 6px;
}
.auth-subtitle {
	font-size: 14px;
	color: var(--castr-text-muted);
	text-align: center;
}
.ff-phone-label {
	display: block;
	font-weight: 500;
	font-size: 11px;
	letter-spacing: 0.02em;
	color: var(--castr-text-muted);
	margin-bottom: 6px;
	margin-left: 2px;
}
.auth-error {
	margin-top: 6px;
	font-size: 12px;
	color: var(--castr-danger);
}
.auth-footer {
	margin-top: 24px;
	text-align: center;
	font-size: 14px;
	color: var(--castr-text-muted);
}
.auth-support {
	margin-top: 8px;
	text-align: center;
	font-size: 12px;
	color: var(--castr-text-muted);
}
.auth-support__link {
	color: var(--castr-text-muted);
	text-decoration: underline;
}
.auth-support__link:hover {
	color: var(--castr-text);
}
.auth-link {
	color: var(--castr-blue-soft);
	font-weight: 600;
}
.auth-link:hover {
	text-decoration: underline;
}
.auth-ghost-btn {
	color: var(--castr-text-muted);
	background: none;
	border: none;
	padding: 0;
}
.auth-ghost-btn:hover {
	color: var(--castr-text);
}
.auth-resend {
	text-align: center;
	font-size: 13px;
	color: var(--castr-text-muted);
}
.auth-resend button {
	background: none;
	border: none;
	padding: 0;
	font-size: 13px;
}
.auth-resend__wait {
	color: var(--castr-text-muted);
	font-variant-numeric: tabular-nums;
}
.auth-spam-hint {
	text-align: center;
	font-size: 12px;
	color: var(--castr-text-muted);
}
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.4s ease;
}
.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}
.auth-legal {
	max-width: 384px;
	text-align: center;
	font-size: 11px;
	line-height: 1.5;
	color: var(--castr-text-muted);
	padding: 0 12px;
}
.auth-legal a {
	color: var(--castr-text-muted);
	text-decoration: underline;
}
.auth-legal a:hover {
	color: var(--castr-text);
}
/* Primary action, matched to the marketing hero CTA. The radius needs
   !important because FormButton hardcodes `!rounded-md`. */
.auth-card :deep(button.form-button) {
	font-size: 14px;
	font-weight: 600;
	letter-spacing: 0.01em;
	border-radius: 12px !important;
}
.auth-card :deep(button.bg-main:not(:disabled)) {
	box-shadow: 0 10px 30px -8px rgba(0, 98, 255, 0.5);
	transition:
		box-shadow 0.2s ease,
		transform 0.12s ease,
		background-color 0.15s ease;
}
.auth-card :deep(button.bg-main:not(:disabled):hover) {
	transform: translateY(-1px);
	box-shadow: 0 14px 36px -8px rgba(0, 98, 255, 0.6);
}
.auth-card :deep(button.bg-main:not(:disabled):active) {
	transform: translateY(0);
	box-shadow: 0 6px 18px -8px rgba(0, 98, 255, 0.5);
}
.auth-card :deep(button:disabled) {
	background-color: rgba(255, 255, 255, 0.06) !important;
	color: var(--castr-text-muted) !important;
	cursor: not-allowed;
	box-shadow: none;
}
</style>

<style>
/* The auth routes are fullScreen, but #app and the always-mounted AppFooter
   still paint the navy app shell behind the card. Scoped to :has() so the
   override lifts automatically on route change. */
body:has(.auth-page),
body:has(.auth-page) #app {
	background-color: var(--castr-bg);
}
body:has(.auth-page) .footer span {
	color: var(--castr-text-muted);
}

/* Align the vue-tel-input control with the marketing form fields. */
.auth-phone.vue-tel-input {
	border-radius: 12px;
	border: 1px solid var(--castr-border);
	background-color: var(--castr-field);
	height: 46px;
	box-shadow: none;
	transition:
		border-color 0.15s ease,
		box-shadow 0.15s ease;
}
.auth-phone.vue-tel-input:hover:not(:focus-within) {
	border-color: var(--castr-border-strong);
}
.auth-phone.vue-tel-input:focus-within {
	border-color: var(--castr-blue);
	box-shadow: 0 0 0 3px var(--castr-focus-ring);
}
.auth-phone--error.vue-tel-input {
	border-color: var(--castr-danger);
}
/* Transparent so the wrapper's translucent fill is not layered twice. */
.auth-phone .vti__input {
	background-color: transparent !important;
	color: var(--castr-text);
	border: none;
	border-radius: 0 12px 12px 0;
	font-size: 15px;
	padding-left: 12px;
}
.auth-phone .vti__input::placeholder {
	color: var(--castr-text-muted);
}
.auth-phone .vti__dropdown {
	background-color: transparent;
	color: var(--castr-text);
	border-radius: 12px 0 0 12px;
	padding: 0 10px 0 12px;
	border-right: 1px solid var(--castr-border);
}
.auth-phone .vti__dropdown:hover,
.auth-phone .vti__dropdown.open {
	background-color: rgba(255, 255, 255, 0.08);
}
.auth-phone .vti__dropdown-arrow {
	color: var(--castr-text-muted);
}
/* Country dropdown panel */
.auth-phone .vti__dropdown-list {
	background-color: var(--castr-field-solid);
	border: 1px solid var(--castr-border);
	border-radius: 12px;
	box-shadow: 0 16px 36px -10px rgba(0, 0, 0, 0.8);
	width: 340px;
	max-width: 80vw;
	z-index: 30;
}
.auth-phone .vti__dropdown-list.below {
	top: 46px;
}
/* Search box */
.auth-phone .vti__search_box {
	width: calc(100% - 16px);
	margin: 8px;
	padding: 9px 12px;
	background-color: rgba(255, 255, 255, 0.06);
	border: 1px solid var(--castr-border);
	border-radius: 8px;
	color: var(--castr-text);
	font-size: 14px;
	outline: none;
}
.auth-phone .vti__search_box:focus {
	border-color: var(--castr-blue);
	box-shadow: 0 0 0 3px var(--castr-focus-ring);
}
.auth-phone .vti__search_box::placeholder {
	color: var(--castr-text-muted);
}
/* Country rows */
.auth-phone .vti__dropdown-item {
	padding: 8px 12px;
	font-size: 14px;
	color: var(--castr-text);
}
.auth-phone .vti__dropdown-item.highlighted {
	background-color: rgba(255, 255, 255, 0.08);
}
.auth-phone .vti__dropdown-item .vti__dropdown-arrow {
	color: var(--castr-text-muted);
}
.auth-phone .vti__dropdown-item strong {
	font-weight: 600;
}
</style>
